import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Moon, Sun, Clock, Globe, Download, Wifi, WifiOff, Heart, Coffee, CalendarPlus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";

// Translations
const translations = {
  en: {
    appName: "Lunar SleepSpace",
    tagline: "Perfect your sleep, naturally",
    wakeUpAt: "Wake up at",
    sleepNow: "Sleep now",
    powerNap: "Power nap",
    wakeUpTabDesc: "When do you want to wake up?",
    sleepNowTabDesc: "Going to bed now?",
    powerNapTabDesc: "Need a quick reset now?",
    calculate: "Calculate bedtimes",
    calculateWake: "Calculate wake times",
    goToSleep: "Go to sleep at",
    setAlarmFor: "Set alarm for",
    cycles: "cycles",
    hours: "hours",
    hoursShort: "h",
    minutesShort: "min",
    fallAsleepSuffix: "to fall asleep",
    recommended: "Recommended",
    benefitTexts: {
      6: "Full recovery: maximum physical and mental restoration",
      5: "Optimal balance: you'll wake up refreshed and alert",
      4: "Adequate rest: good for short-term recovery",
      3: "Minimum rest: only for emergencies"
    },
    setReminder: "Set reminder",
    reminderSet: "Reminder set!",
    reminderOff: "Reminder cancelled",
    notificationPermission: "Please enable notifications in your browser",
    notificationTitle: "Time to sleep!",
    notificationBody: "It's time to go to bed to get {cycles} sleep cycles",
    fallAsleepNote: "Includes 15 min to fall asleep",
    currentTime: "Current time",
    installApp: "Install app",
    offline: "Offline",
    online: "Online",
    kofiSupport: "Support me on Ko-fi",
    scienceDisclaimer:
      "Indicative times use ~90 min cycles and ~15 min sleep onset as typical adult averages; actual sleep varies between people and nights.",
    scienceSourcesTitle: "Sources:",
    scienceLinkNcbiLabel: "StatPearls — Sleep stages (NCBI)",
    scienceLinkNhlbiLabel: "NHLBI (NIH) — Stages of sleep",
    napTitle: "Wake me up at",
    napDisclaimer:
      "Best evidence supports short naps (10-20 min) for fast alertness. 30 min can increase sleep inertia; ~90 min may reduce inertia by completing a full cycle.",
    napSourcesTitle: "Power nap sources:",
    napSourceBriefLabel: "Brooks & Lack, SLEEP (2006)",
    napSourceInertiaLabel: "Hilditch et al., SLEEP (2016)",
    napOption10Title: "Quick reset",
    napOption10Desc: "Fast boost, usually minimal grogginess",
    napOption20Title: "Power nap",
    napOption20Desc: "Strong alertness boost, slight wake-up delay",
    napOption90Title: "Full cycle nap",
    napOption90Desc: "Longer recovery when you're very tired",
    napPrep: "Includes ~5 min to fall asleep",
    calendarTitleBedtime: "It's time to go to sleep!",
    calendarTitleWake: "Good morning!",
    calendarTitleNap: "Nap done?",
    calendarDescription: "Created by Lunar SleepSpace",
    calendarAddCta: "Add to calendar",
    calendarConsentPrompt:
      "Allow Lunar SleepSpace to add events to your calendar flow automatically (Google Calendar page or .ics fallback)? Your choice will be saved."
  },
  it: {
    appName: "Lunar SleepSpace",
    tagline: "Perfeziona il tuo sonno, naturalmente",
    wakeUpAt: "Sveglia alle",
    sleepNow: "Dormi ora",
    powerNap: "Power nap",
    wakeUpTabDesc: "A che ora vuoi svegliarti?",
    sleepNowTabDesc: "Stai andando a dormire ora?",
    powerNapTabDesc: "Hai bisogno di ricaricarti adesso?",
    calculate: "Calcola orari",
    calculateWake: "Calcola sveglie",
    goToSleep: "Vai a dormire alle",
    setAlarmFor: "Imposta sveglia alle",
    cycles: "cicli",
    hours: "ore",
    hoursShort: "h",
    minutesShort: "min",
    fallAsleepSuffix: "per addormentarsi",
    recommended: "Consigliato",
    benefitTexts: {
      6: "Recupero completo: massimo ripristino fisico e mentale",
      5: "Equilibrio ottimale: ti sveglierai riposato e vigile",
      4: "Riposo adeguato: buono per recuperi a breve termine",
      3: "Riposo minimo: solo per emergenze"
    },
    setReminder: "Imposta promemoria",
    reminderSet: "Promemoria impostato!",
    reminderOff: "Promemoria cancellato",
    notificationPermission: "Abilita le notifiche nel browser",
    notificationTitle: "È ora di dormire!",
    notificationBody: "È ora di andare a letto per ottenere {cycles} cicli di sonno",
    fallAsleepNote: "Include 15 min per addormentarsi",
    currentTime: "Ora attuale",
    installApp: "Installa app",
    offline: "Offline",
    online: "Online",
    kofiSupport: "Supportami su Ko-fi",
    scienceDisclaimer:
      "Gli orari indicativi usano cicli di ~90 min e ~15 min per addormentarsi come medie tipiche negli adulti; il sonno reale varia tra persone e notti.",
    scienceSourcesTitle: "Fonti:",
    scienceLinkNcbiLabel: "StatPearls — Fasi del sonno (NCBI)",
    scienceLinkNhlbiLabel: "NHLBI (NIH) — Fasi del sonno",
    napTitle: "Metti la sveglia alle",
    napDisclaimer:
      "Le evidenze migliori supportano sonnellini brevi (10-20 min) per ripresa rapida. I 30 min aumentano il rischio di sleep inertia; ~90 min possono ridurla completando un ciclo.",
    napSourcesTitle: "Fonti power nap:",
    napSourceBriefLabel: "Brooks & Lack, SLEEP (2006)",
    napSourceInertiaLabel: "Hilditch et al., SLEEP (2016)",
    napOption10Title: "Reset rapido",
    napOption10Desc: "Spinta rapida, di solito poca sonnolenza al risveglio",
    napOption20Title: "Power nap",
    napOption20Desc: "Buon boost di vigilanza, possibile lieve ritardo",
    napOption90Title: "Ciclo completo",
    napOption90Desc: "Recupero più profondo quando sei molto stanco",
    napPrep: "Include ~5 min per addormentarsi",
    calendarTitleBedtime: "È ora di andare a dormire!",
    calendarTitleWake: "Buongiorno!",
    calendarTitleNap: "Fatto il riposino?",
    calendarDescription: "Creato da Lunar SleepSpace",
    calendarAddCta: "Aggiungi al calendario",
    calendarConsentPrompt:
      "Consenti a Lunar SleepSpace di aggiungere automaticamente eventi nel flusso calendario (pagina Google Calendar o fallback .ics)? La scelta verra salvata."
  }
};

/** Peer-reviewed / government references for sleep architecture and cycles */
const SCIENCE_REF_NCBI_STATPEARLS =
  "https://www.ncbi.nlm.nih.gov/books/NBK526132/";
const SCIENCE_REF_NHLBI = "https://www.nhlbi.nih.gov/health/sleep/stages-of-sleep";
const SCIENCE_REF_POWERNAP_BRIEF = "https://doi.org/10.1093/sleep/29.6.831";
const SCIENCE_REF_POWERNAP_INERTIA = "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4763354/";

const ScienceDisclaimerNote = ({ t }) => (
  <div
    className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-center text-[11px] leading-snug text-slate-500"
    role="note"
  >
    <p className="mb-2">{t.scienceDisclaimer}</p>
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-slate-600">
      <span className="text-slate-500">{t.scienceSourcesTitle}</span>
      <a
        href={SCIENCE_REF_NCBI_STATPEARLS}
        className="text-violet-400/90 underline decoration-violet-400/40 underline-offset-2 transition-colors hover:text-violet-300"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t.scienceLinkNcbiLabel}
      </a>
      <span className="text-slate-600" aria-hidden>
        ·
      </span>
      <a
        href={SCIENCE_REF_NHLBI}
        className="text-violet-400/90 underline decoration-violet-400/40 underline-offset-2 transition-colors hover:text-violet-300"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t.scienceLinkNhlbiLabel}
      </a>
    </p>
  </div>
);

const PowerNapDisclaimerNote = ({ t }) => (
  <div
    className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-center text-[11px] leading-snug text-slate-500"
    role="note"
  >
    <p className="mb-2">{t.napDisclaimer}</p>
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-slate-600">
      <span className="text-slate-500">{t.napSourcesTitle}</span>
      <a
        href={SCIENCE_REF_POWERNAP_BRIEF}
        className="text-violet-400/90 underline decoration-violet-400/40 underline-offset-2 transition-colors hover:text-violet-300"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t.napSourceBriefLabel}
      </a>
      <span className="text-slate-600" aria-hidden>
        ·
      </span>
      <a
        href={SCIENCE_REF_POWERNAP_INERTIA}
        className="text-violet-400/90 underline decoration-violet-400/40 underline-offset-2 transition-colors hover:text-violet-300"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t.napSourceInertiaLabel}
      </a>
    </p>
  </div>
);

const KOFI_WIDGET_SCRIPT = "https://storage.ko-fi.com/cdn/widget/Widget_2.js";
const KOFI_PAGE_ID = "V7V31ULOT1";
/** Ko-fi button fill (violet-500; matches preview) */
const KOFI_ACCENT_COLOR = "#8b5cf6";

// Sleep cycle constants
const CYCLE_MINUTES = 90;
const FALL_ASLEEP_MINUTES = 15;
const NAP_FALL_ASLEEP_MINUTES = 5;
const POWER_NAP_OPTIONS = [
  { key: "napOption10", durationMinutes: 10, recommended: true },
  { key: "napOption20", durationMinutes: 20, recommended: false },
  { key: "napOption90", durationMinutes: 90, recommended: false }
];
const CALENDAR_CONSENT_KEY = "lunarsleep-calendar-consent";

// Calculate bedtimes given a wake time
const calculateBedtimes = (wakeHour, wakeMinute) => {
  const results = [];
  const cycleOptions = [6, 5, 4, 3];
  
  for (const cycles of cycleOptions) {
    const totalMinutes = cycles * CYCLE_MINUTES + FALL_ASLEEP_MINUTES;
    
    let bedMinutes = wakeMinute - (totalMinutes % 60);
    let bedHours = wakeHour - Math.floor(totalMinutes / 60);
    
    if (bedMinutes < 0) {
      bedMinutes += 60;
      bedHours -= 1;
    }
    
    if (bedHours < 0) {
      bedHours += 24;
    }
    
    results.push({
      cycles,
      time: {
        hour: bedHours,
        minute: bedMinutes
      },
      isRecommended: cycles === 5
    });
  }
  
  return results;
};

// Calculate wake times given current time
const calculateWakeTimes = (currentHour, currentMinute) => {
  const results = [];
  const cycleOptions = [3, 4, 5, 6];
  
  for (const cycles of cycleOptions) {
    const totalMinutes = cycles * CYCLE_MINUTES + FALL_ASLEEP_MINUTES;
    
    let wakeMinutes = currentMinute + (totalMinutes % 60);
    let wakeHours = currentHour + Math.floor(totalMinutes / 60);
    
    if (wakeMinutes >= 60) {
      wakeMinutes -= 60;
      wakeHours += 1;
    }
    
    if (wakeHours >= 24) {
      wakeHours -= 24;
    }
    
    results.push({
      cycles,
      time: {
        hour: wakeHours,
        minute: wakeMinutes
      },
      isRecommended: cycles === 5
    });
  }
  
  return results;
};

const calculatePowerNapWakeTimes = (currentHour, currentMinute) => {
  const results = [];
  for (const option of POWER_NAP_OPTIONS) {
    const totalMinutes = option.durationMinutes + NAP_FALL_ASLEEP_MINUTES;
    let wakeMinutes = currentMinute + (totalMinutes % 60);
    let wakeHours = currentHour + Math.floor(totalMinutes / 60);

    if (wakeMinutes >= 60) {
      wakeMinutes -= 60;
      wakeHours += 1;
    }
    if (wakeHours >= 24) {
      wakeHours -= 24;
    }

    results.push({
      key: option.key,
      durationMinutes: option.durationMinutes,
      time: { hour: wakeHours, minute: wakeMinutes },
      isRecommended: option.recommended
    });
  }
  return results;
};

// Format time for display
const formatTime = (hour, minute) => {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

/** Duration from minutes only (sleep cycles), shown separately from fall-asleep minutes */
const formatDurationHm = (totalMinutes, t) => {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}${t.minutesShort}`;
  if (m === 0) return `${h}${t.hoursShort}`;
  return `${h}${t.hoursShort} ${m}${t.minutesShort}`;
};

const toCalendarTimestampUTC = (date) =>
  date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

const buildIcsContent = ({ title, details, startDate, endDate }) => {
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@lunarsleepspace`;
  const dtStamp = toCalendarTimestampUTC(new Date());
  const dtStart = toCalendarTimestampUTC(startDate);
  const dtEnd = toCalendarTimestampUTC(endDate);
  const escapedTitle = title.replace(/\n/g, "\\n").replace(/,/g, "\\,");
  const escapedDetails = details.replace(/\n/g, "\\n").replace(/,/g, "\\,");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Lunar SleepSpace//Calendar Event//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escapedTitle}`,
    `DESCRIPTION:${escapedDetails}`,
    "BEGIN:VALARM",
    "TRIGGER:PT0M",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapedTitle}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR"
  ].join("\r\n");
};

const downloadIcs = (eventData) => {
  const blob = new Blob([buildIcsContent(eventData)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "lunarsleepspace-event.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// TimePicker Component
const TimePicker = ({ hour, minute, onHourChange, onMinuteChange }) => {
  const [hourInput, setHourInput] = useState(hour.toString().padStart(2, '0'));
  const [minuteInput, setMinuteInput] = useState(minute.toString().padStart(2, '0'));
  const [hourFocused, setHourFocused] = useState(false);
  const [minuteFocused, setMinuteFocused] = useState(false);

  // Sync with parent state when not focused
  useEffect(() => {
    if (!hourFocused) {
      setHourInput(hour.toString().padStart(2, '0'));
    }
  }, [hour, hourFocused]);

  useEffect(() => {
    if (!minuteFocused) {
      setMinuteInput(minute.toString().padStart(2, '0'));
    }
  }, [minute, minuteFocused]);

  const handleHourChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setHourInput(value);
    
    if (value !== '') {
      const num = parseInt(value, 10);
      if (num >= 0 && num <= 23) {
        onHourChange(num);
      }
    }
  };

  const handleMinuteChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setMinuteInput(value);
    
    if (value !== '') {
      const num = parseInt(value, 10);
      if (num >= 0 && num <= 59) {
        onMinuteChange(num);
      }
    }
  };

  const handleHourBlur = () => {
    setHourFocused(false);
    // On blur, if empty or invalid, reset to current value
    if (hourInput === '' || parseInt(hourInput, 10) > 23) {
      setHourInput(hour.toString().padStart(2, '0'));
    } else {
      const num = parseInt(hourInput, 10);
      onHourChange(num);
      setHourInput(num.toString().padStart(2, '0'));
    }
  };

  const handleMinuteBlur = () => {
    setMinuteFocused(false);
    // On blur, if empty or invalid, reset to current value
    if (minuteInput === '' || parseInt(minuteInput, 10) > 59) {
      setMinuteInput(minute.toString().padStart(2, '0'));
    } else {
      const num = parseInt(minuteInput, 10);
      onMinuteChange(num);
      setMinuteInput(num.toString().padStart(2, '0'));
    }
  };

  const handleHourFocus = (e) => {
    setHourFocused(true);
    e.target.select();
  };

  const handleMinuteFocus = (e) => {
    setMinuteFocused(true);
    e.target.select();
  };

  return (
    <div className="flex items-center justify-center gap-2 py-8" data-testid="time-picker">
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={hourInput}
        onChange={handleHourChange}
        onFocus={handleHourFocus}
        onBlur={handleHourBlur}
        className="time-input text-5xl sm:text-6xl w-20 sm:w-24"
        data-testid="time-picker-hour"
        aria-label="Hours"
      />
      <span className="text-4xl sm:text-5xl text-slate-600 font-light mb-2 font-outfit">:</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={minuteInput}
        onChange={handleMinuteChange}
        onFocus={handleMinuteFocus}
        onBlur={handleMinuteBlur}
        className="time-input text-5xl sm:text-6xl w-20 sm:w-24"
        data-testid="time-picker-minute"
        aria-label="Minutes"
      />
    </div>
  );
};

// Result Card Component
const ResultCard = ({ result, t, type, onAddToCalendar }) => {
  return (
    <div 
      className={`result-card visible relative overflow-hidden p-4 rounded-2xl border flex items-center justify-between transition-all ${
        result.isRecommended 
          ? 'recommended-card border-violet-400/50 bg-violet-950/20' 
          : 'border-white/5 bg-white/5 hover:bg-white/10'
      }`}
      data-testid={`result-card-${result.cycles}-cycles`}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <span className="text-2xl sm:text-3xl font-outfit font-medium text-white" data-testid={`result-time-${result.cycles}`}>
            {formatTime(result.time.hour, result.time.minute)}
          </span>
          {result.isRecommended && (
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-violet-500/20 text-violet-300" data-testid="recommended-badge">
              {t.recommended}
            </span>
          )}
        </div>
        <span className="text-sm text-slate-400">
          {result.cycles} {t.cycles} · {formatDurationHm(result.cycles * CYCLE_MINUTES, t)}
        </span>
        <span className="text-xs text-slate-500">
          + {FALL_ASLEEP_MINUTES} {t.minutesShort} {t.fallAsleepSuffix}
        </span>
        <span className="text-xs text-slate-500 mt-1">
          {t.benefitTexts[result.cycles]}
        </span>
      </div>
      
      <button
        onClick={() => onAddToCalendar(result, type)}
        className="p-2 rounded-xl transition-all bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"
        aria-label={t.calendarAddCta}
        title={t.calendarAddCta}
      >
        <CalendarPlus className="w-5 h-5" />
      </button>
    </div>
  );
};

const PowerNapCard = ({ result, t, onAddToCalendar }) => (
  <div
    className={`result-card visible relative overflow-hidden p-4 rounded-2xl border transition-all ${
      result.isRecommended
        ? "recommended-card border-violet-400/50 bg-violet-950/20"
        : "border-white/5 bg-white/5 hover:bg-white/10"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="flex items-center gap-3">
          <span className="text-2xl sm:text-3xl font-outfit font-medium text-white">
            {formatTime(result.time.hour, result.time.minute)}
          </span>
          {result.isRecommended && (
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-violet-500/20 text-violet-300">
              {t.recommended}
            </span>
          )}
        </div>
        <span className="text-sm text-slate-400">
          {t[`${result.key}Title`]} · {result.durationMinutes} {t.minutesShort}
        </span>
        <p className="text-xs text-slate-500 mt-1">{t[`${result.key}Desc`]}</p>
      </div>
      <button
        onClick={() => onAddToCalendar(result, "powernap")}
        className="p-2 rounded-xl transition-all bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"
        aria-label={t.calendarAddCta}
        title={t.calendarAddCta}
      >
        <CalendarPlus className="w-5 h-5" />
      </button>
    </div>
  </div>
);

// Main app shell
const LunarSleepSpace = () => {
  // State
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('lunarsleep-lang');
    return saved || 'it';
  });
  
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('lunarsleep-tab');
    return saved || 'wakeup';
  });
  
  const [wakeHour, setWakeHour] = useState(() => {
    const saved = localStorage.getItem('lunarsleep-wake-hour');
    return saved ? parseInt(saved, 10) : 7;
  });
  
  const [wakeMinute, setWakeMinute] = useState(() => {
    const saved = localStorage.getItem('lunarsleep-wake-minute');
    return saved ? parseInt(saved, 10) : 0;
  });
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [results, setResults] = useState([]);
  const [napResults, setNapResults] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [kofiHtml, setKofiHtml] = useState("");
  const [calendarConsent, setCalendarConsent] = useState(() => {
    return localStorage.getItem(CALENDAR_CONSENT_KEY) || "unknown";
  });

  const t = translations[lang];

  useEffect(() => {
    const apply = () => {
      if (typeof window.kofiwidget2 === "undefined") return;
      const strings = translations[lang];
      window.kofiwidget2.init(strings.kofiSupport, KOFI_ACCENT_COLOR, KOFI_PAGE_ID);
      setKofiHtml(window.kofiwidget2.getHTML());
    };

    if (window.kofiwidget2) {
      apply();
      return;
    }

    const existing = document.querySelector(`script[src="${KOFI_WIDGET_SCRIPT}"]`);
    if (existing) {
      if (window.kofiwidget2) {
        apply();
      } else {
        existing.addEventListener("load", apply, { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = KOFI_WIDGET_SCRIPT;
    script.async = true;
    script.onload = apply;
    document.body.appendChild(script);
  }, [lang]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle PWA install
  const handleInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success(lang === 'it' ? 'App installata!' : 'App installed!');
    }
    setInstallPrompt(null);
  };

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('lunarsleep-lang', lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem('lunarsleep-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('lunarsleep-wake-hour', wakeHour.toString());
  }, [wakeHour]);

  useEffect(() => {
    localStorage.setItem('lunarsleep-wake-minute', wakeMinute.toString());
  }, [wakeMinute]);

  // Calculate results when inputs change
  const handleCalculate = useCallback(() => {
    if (activeTab === 'wakeup') {
      setResults(calculateBedtimes(wakeHour, wakeMinute));
    } else if (activeTab === "sleepnow") {
      const now = new Date();
      setResults(calculateWakeTimes(now.getHours(), now.getMinutes()));
    } else {
      const now = new Date();
      setNapResults(calculatePowerNapWakeTimes(now.getHours(), now.getMinutes()));
    }
  }, [activeTab, wakeHour, wakeMinute]);

  // Auto-calculate on tab change for "sleep now"
  useEffect(() => {
    if (activeTab === 'sleepnow' || activeTab === "powernap") {
      handleCalculate();
    }
  }, [activeTab, handleCalculate]);

  // Toggle language
  const toggleLang = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const ensureCalendarConsent = () => {
    if (calendarConsent === "granted") return true;
    const granted = window.confirm(t.calendarConsentPrompt);
    const nextValue = granted ? "granted" : "denied";
    setCalendarConsent(nextValue);
    localStorage.setItem(CALENDAR_CONSENT_KEY, nextValue);
    return granted;
  };

  const getNextOccurrence = (hour, minute) => {
    const now = new Date();
    const eventDate = new Date(now);
    eventDate.setHours(hour, minute, 0, 0);
    if (eventDate <= now) {
      eventDate.setDate(eventDate.getDate() + 1);
    }
    return eventDate;
  };

  const handleAddToCalendar = (result, type) => {
    if (!ensureCalendarConsent()) return;

    const titleMap = {
      bedtime: t.calendarTitleBedtime,
      waketime: t.calendarTitleWake,
      powernap: t.calendarTitleNap
    };
    const title = titleMap[type] || t.calendarTitleBedtime;
    const details = `${t.calendarDescription}\nLunar SleepSpace`;
    const startDate = getNextOccurrence(result.time.hour, result.time.minute);
    const endDate = new Date(startDate.getTime() + 5 * 60 * 1000);
    const eventData = { title, details, startDate, endDate };

    // Use .ics on all platforms so reminder defaults are embedded (at event time).
    downloadIcs(eventData);
    toast.success(t.calendarAddCta);
  };

  return (
    <div className="app-container">
      <div className="max-w-md mx-auto flex min-h-screen flex-col px-4 pb-28 pt-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1.5">
            <img
              src={`${process.env.PUBLIC_URL}/header-moon.png`}
              alt=""
              width={64}
              height={64}
              className="relative left-[5px] top-[4px] h-16 w-16 shrink-0 moon-float object-contain"
              aria-hidden
            />
            <div>
              <h1 className="text-xl font-outfit font-semibold text-white" data-testid="app-title">
                {t.appName}
              </h1>
              <p className="text-xs text-slate-500">{t.tagline}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Offline indicator */}
            {!isOnline && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400 text-xs" data-testid="offline-indicator">
                <WifiOff className="w-3 h-3" />
                <span>{t.offline}</span>
              </div>
            )}
            
            {/* Install button */}
            {installPrompt && !isInstalled && (
              <button
                onClick={handleInstall}
                className="lang-toggle px-3 py-2 rounded-xl flex items-center gap-2 text-sm text-violet-400 border-violet-400/30"
                data-testid="install-btn"
                aria-label={t.installApp}
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="lang-toggle px-3 py-2 rounded-xl flex items-center gap-2 text-sm text-slate-300"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{lang}</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="custom-tabs p-1 rounded-2xl mb-6" data-testid="tabs-list">
              <TabsTrigger 
                value="wakeup" 
                className="custom-tab flex-1 py-3 rounded-xl text-sm font-medium gap-2"
                data-testid="tab-wakeup"
              >
                <Sun className="w-4 h-4" />
                {t.wakeUpAt}
              </TabsTrigger>
              <TabsTrigger 
                value="sleepnow" 
                className="custom-tab flex-1 py-3 rounded-xl text-sm font-medium gap-2"
                data-testid="tab-sleepnow"
              >
                <Moon className="w-4 h-4" />
                {t.sleepNow}
              </TabsTrigger>
              <TabsTrigger
                value="powernap"
                className="custom-tab flex-1 py-3 rounded-xl text-sm font-medium gap-2"
                data-testid="tab-powernap"
              >
                <Coffee className="w-4 h-4" />
                {t.powerNap}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="wakeup" className="flex-1 flex flex-col mt-0">
              <div className="glass-card rounded-3xl p-6 mb-6">
                <p className="text-sm text-slate-400 text-center mb-2">{t.wakeUpTabDesc}</p>
                <TimePicker
                  hour={wakeHour}
                  minute={wakeMinute}
                  onHourChange={setWakeHour}
                  onMinuteChange={setWakeMinute}
                />
                <Button
                  onClick={handleCalculate}
                  className="btn-primary w-full py-4 rounded-2xl text-white font-semibold text-base"
                  data-testid="calculate-btn"
                >
                  <Clock className="w-5 h-5" />
                  {t.calculate}
                </Button>
              </div>

              {results.length > 0 && (
                <div className="space-y-3" data-testid="results-container">
                  <ScienceDisclaimerNote t={t} />
                  <div className="text-sm text-slate-400 space-y-1">
                    <p>{t.goToSleep}</p>
                    <p className="text-xs text-slate-600">{t.fallAsleepNote}</p>
                  </div>
                  {results.map((result) => (
                    <ResultCard
                      key={result.cycles}
                      result={result}
                      t={t}
                      type="bedtime"
                      onAddToCalendar={handleAddToCalendar}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sleepnow" className="flex-1 flex flex-col mt-0">
              <div className="glass-card rounded-3xl p-6 mb-6">
                <p className="text-sm text-slate-400 text-center mb-2">{t.sleepNowTabDesc}</p>
                
                <div className="flex items-center justify-center gap-2 py-8">
                  <Clock className="w-6 h-6 text-violet-400" />
                  <span className="text-5xl sm:text-6xl font-outfit font-light text-white" data-testid="current-time-display">
                    {formatTime(currentTime.getHours(), currentTime.getMinutes())}
                  </span>
                </div>
                
                <p className="text-xs text-slate-500 text-center mb-4">{t.currentTime}</p>
                
                <Button
                  onClick={handleCalculate}
                  className="btn-primary w-full py-4 rounded-2xl text-white font-semibold text-base"
                  data-testid="calculate-wake-btn"
                >
                  <Clock className="w-5 h-5" />
                  {t.calculateWake}
                </Button>
              </div>

              {results.length > 0 && (
                <div className="space-y-3" data-testid="wake-results-container">
                  <ScienceDisclaimerNote t={t} />
                  <div className="text-sm text-slate-400 space-y-1">
                    <p>{t.setAlarmFor}</p>
                    <p className="text-xs text-slate-600">{t.fallAsleepNote}</p>
                  </div>
                  {results.map((result) => (
                    <ResultCard
                      key={result.cycles}
                      result={result}
                      t={t}
                      type="waketime"
                      onAddToCalendar={handleAddToCalendar}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="powernap" className="flex-1 flex flex-col mt-0">
              <div className="glass-card rounded-3xl p-6 mb-6">
                <p className="text-sm text-slate-400 text-center mb-2">{t.powerNapTabDesc}</p>
                <div className="flex items-center justify-center gap-2 py-8">
                  <Clock className="w-6 h-6 text-violet-400" />
                  <span className="text-5xl sm:text-6xl font-outfit font-light text-white">
                    {formatTime(currentTime.getHours(), currentTime.getMinutes())}
                  </span>
                </div>
                <p className="text-xs text-slate-500 text-center mb-4">{t.currentTime}</p>
                <Button
                  onClick={handleCalculate}
                  className="btn-primary w-full py-4 rounded-2xl text-white font-semibold text-base"
                  data-testid="calculate-nap-btn"
                >
                  <Clock className="w-5 h-5" />
                  {t.powerNap}
                </Button>
              </div>

              {napResults.length > 0 && (
                <div className="space-y-3" data-testid="nap-results-container">
                  <PowerNapDisclaimerNote t={t} />
                  <div className="text-sm text-slate-400 space-y-1">
                    <p>{t.napTitle}</p>
                    <p className="text-xs text-slate-600">{t.napPrep}</p>
                  </div>
                  {napResults.map((result) => (
                    <PowerNapCard
                      key={result.key}
                      result={result}
                      t={t}
                      onAddToCalendar={handleAddToCalendar}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </main>

      </div>

      {/* Pinned footer: compact rounded “island” */}
      <footer className="app-footer-pinned">
        <div className="app-footer-island mx-auto max-w-md space-y-1.5 rounded-2xl border border-white/10 bg-[#05070a]/90 px-3 py-2 text-center shadow-[0_8px_32px_rgba(0,0,0,0.35)] backdrop-blur-md">
          <p className="flex flex-wrap items-center justify-center gap-1 text-[11px] leading-tight text-slate-500">
            Lunar SleepSpace è senza pubblicita ed è gratis (ma non per me 😔) quindi se vuoi:
          </p>
          <div
            className="kofi-widget-wrap flex justify-center [&_a.kofi-button]:transition-opacity"
            dangerouslySetInnerHTML={{ __html: kofiHtml }}
          />
          <p className="flex flex-wrap items-center justify-center gap-1 text-[11px] leading-tight text-slate-500">
            Made with love in 2026 by TakaDev
            <Heart
              className="h-3 w-3 shrink-0 fill-violet-500 text-violet-500"
              aria-hidden
            />
          </p>
        </div>
      </footer>
      
      <Toaster 
        position="top-center" 
        richColors 
        theme="dark"
        toastOptions={{
          style: {
            background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#f8fafc'
          }
        }}
      />
    </div>
  );
};

// Register Service Worker for PWA
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      // Prevent duplicate listeners/intervals in edge cases.
      if (window.__lunarsleepspaceSwInit) return;
      window.__lunarsleepspaceSwInit = true;

      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      let isRefreshing = false;
      const RELOAD_GUARD_MS = 30 * 1000;
      const UPDATE_CHECK_INTERVAL_MS = 3 * 60 * 1000;

      const activateUpdate = (worker) => {
        if (!worker) return;
        worker.postMessage({ type: 'SKIP_WAITING' });
      };

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (isRefreshing) return;
        const lastReloadAt = Number(sessionStorage.getItem('sw-last-reload-at') || '0');
        if (Date.now() - lastReloadAt < RELOAD_GUARD_MS) return;
        isRefreshing = true;
        sessionStorage.setItem('sw-last-reload-at', String(Date.now()));
        window.location.reload();
      });
      
      if (registration.installing) {
        console.log('Service worker installing');
      } else if (registration.waiting) {
        console.log('Service worker waiting, activating update');
        activateUpdate(registration.waiting);
      } else if (registration.active) {
        console.log('Service worker active');
      }

      // Periodically check for updates while app is open
      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_CHECK_INTERVAL_MS);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New content available, activating update');
              activateUpdate(newWorker);
            }
          });
        }
      });
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }
};

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LunarSleepSpace />} />
        <Route path="*" element={<LunarSleepSpace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
